import { useHabitStore } from '../stores/habitStore';
import { useProjectStore } from '../stores/projectStore';
import { useProgramStore } from '../stores/programStore';
import { useQuestStore } from '../stores/questStore';
import { useRoutineStore } from '../stores/routineStore';
import { useAbilitiesStore } from '../stores/abilitiesStore';
import { useJournalStore } from '../stores/journalStore';
import { getAreas } from '../constants/areas';
import { formatDateToLocal } from './date';
import type { View } from '../lib/viewRouter';
import type { HabitSchedule } from '../types';
import { DEFAULT_PROGRAM_DAYS } from '../types/inbox';

// The seven buildable action types (mirrors the Build ribbon). An inbox note can
// be refined into any of these; `task` stays in the task store, the rest create a
// fresh minimal entity (the user fleshes it out in that type's own builder).
export type InboxActionType = 'task' | 'habit' | 'project' | 'program' | 'quest' | 'ability' | 'routine';

// Ordered to match the Build page's tab order (Quests · Abilities · Programs ·
// Projects · Tasks · Habits · Routines).
export const INBOX_ACTION_TYPES: InboxActionType[] = ['quest', 'ability', 'program', 'project', 'task', 'habit', 'routine'];

/**
 * Everything routeInboxAction can build. Journal is a REAL entity but not a
 * Build-hub item, so it stays out of INBOX_ACTION_TYPES (which mirrors the Build
 * ribbon and is reused by the Life Balance results view).
 */
export type RoutableType = InboxActionType | 'journal';

interface RouteInput {
  title: string;
  description?: string;
  category: string;
  /** The inbox item's area id, tagged onto a journal entry when present. */
  area?: string;
  /**
   * The "when" decided at Classify (see WHEN_SPEC in types/inbox).
   * ⚠️ These used to be INVENTED here — a project silently started today and a
   * habit was silently daily. A default is still applied when the user skips the
   * question, but the panel now says which one, so it is a stated assumption
   * rather than a quiet answer given on their behalf.
   */
  scheduledFor?: string;
  scheduledUntil?: string;
  recurrence?: HabitSchedule;
  durationDays?: number;
}

/**
 * Create a real entity of `type` from a refined inbox note (minimal defaults; the
 * user refines further in that type's builder). Returns the hub view it lives in.
 * `task` is handled by the caller via the inbox store; everything else lands here.
 */
export function routeInboxAction(
  type: RoutableType,
  { title, description, category, area, scheduledFor, scheduledUntil, recurrence, durationDays }: RouteInput
): View {
  const today = formatDateToLocal(new Date());
  const desc = description?.trim() || undefined;

  switch (type) {
    case 'habit':
      useHabitStore.getState().addHabit({
        name: title,
        category,
        // Daily remains the fallback, but it is now the one the panel advertised.
        schedule: recurrence ?? { interval: 'daily' },
      });
      return 'habits';
    case 'project':
      useProjectStore.getState().addProject({
        title,
        ...(desc ? { description: desc } : {}),
        category,
        status: 'active',
        // ⚠️ A project's `scheduledFor` is its START — the one destination where
        // the field does not mean "due". WHEN_SPEC labels it "Starts" for exactly
        // this reason; `scheduledUntil` carries the due date.
        startDate: scheduledFor || today,
        ...(scheduledUntil ? { dueDate: scheduledUntil } : {}),
        priority: 'medium',
        complexity: 'medium',
        milestones: [],
      });
      return 'projects';
    case 'program':
      useProgramStore.getState().addProgram({
        userId: 'local-user',
        title,
        ...(desc ? { description: desc } : {}),
        category,
        programStatus: 'draft',
        icon: 'BookOpen',
        // 30 stays the fallback, but it is the one the panel advertised — and it
        // comes from the same constant the copy is built from.
        durationDays: durationDays ?? DEFAULT_PROGRAM_DAYS,
        difficulty: 'beginner',
        estimatedDailyMinutes: 15,
        targetParameters: [],
        modules: [],
        isPublished: false,
        isTemplate: false,
      });
      return 'programs';
    case 'quest': {
      const qs = useQuestStore.getState();
      const folderId =
        [...qs.folders].sort((a, b) => a.order - b.order)[0]?.id ??
        qs.addFolder({ name: 'Main Quests', description: 'Primary objectives and life-changing goals' });
      qs.addQuest({ title, ...(desc ? { description: desc } : {}), category, folderId, status: 'active' });
      return 'quests';
    }
    case 'ability':
      useAbilitiesStore.getState().addAbility({ name: title, description: desc ?? '', category, icon: 'Star', skills: [] });
      return 'abilities';
    case 'journal': {
      // A journal entry is a real, dated record — it just isn't a Build item.
      // ⚠️ Journal entries mint NO XP (they never have), so routing here cannot
      // inflate the economy.
      const areaName = area ? getAreas(category).find(a => a.id === area)?.name : undefined;
      useJournalStore.getState().addEntry({
        title,
        content: desc ?? '',
        date: today,
        energyCharge: 0,
        pleasantnessCharge: 0,
        focusLevel: 0,
        // The inbox's domain/area tags survive the trip as journal tags — the one
        // destination where that context has somewhere real to land.
        tags: [category, areaName].filter(Boolean) as string[],
      });
      return 'journal';
    }
    case 'routine':
      useRoutineStore.getState().addRoutine({
        name: title,
        ...(desc ? { description: desc } : {}),
        category,
        steps: [],
        totalXP: 0,
        totalActiveTime: 0,
        totalDuration: 0,
      });
      return 'routines';
    default:
      return 'tasks';
  }
}
